import * as a from '@mitranim/js/all.mjs'
import * as c from './conf.mjs'
import * as ut from './util-text.mjs'
import * as um from './util-misc.mjs'
import * as uma from './util-math.mjs'
import * as uc from './util-coll.mjs'

export class DateTime extends a.DateValid {
  clone() {return new this.constructor(this)}

  // TODO better name.
  enumMonth() {return Months.all.byValueReq(this.getMonth())}

  // TODO better name.
  enumWeekday() {return Weekdays.all.byValueReq(this.getDay())}

  // TODO: is this redundant? Don't we already have code like this somewhere?
  // TODO: move to `a.DateTime`.
  hourMinStr(lang) {
    if (!this.isValid()) return ``
    return ut.formatDateAsHoursMinutesBySystemSettings(this, lang)
  }

  addYear(val) {
    this.setFullYear(this.getFullYear() + a.reqInt(val))
    return this
  }

  addMonth(val) {
    this.setMonth(this.getMonth() + a.reqInt(val))
    return this
  }

  addDate(val) {
    this.setDate(this.getDate() + a.reqInt(val))
    return this
  }

  get YearMonth() {return YearMonth}
  yearMonth() {return this.YearMonth.fromDateTime(this)}

  get YearMonthDay() {return YearMonthDay}
  yearMonthDay() {return this.YearMonthDay.fromDateTime(this)}
}

class CalEnum extends a.ClsVec {
  type() {throw Error(`must implement in subclass`)}

  static {a.memGet(this)}
  get valueIndex() {return a.index(this, um.getValue)}
  get abbrIndex() {return a.index(this, um.getAbbr)}
  get slugIndex() {return a.index(this, um.getSlug)}

  byValue(key) {return vacSome(key) && this.valueIndex[a.reqNat(key)]}

  byValueReq(key) {
    return (
      this.byValue(key) ||
      a.panic(Error(`unable to find ${this.type()} with value ${a.show(key)}`))
    )
  }

  byAbbr(key) {return vacSome(key) && this.abbrIndex[a.reqStr(key)]}

  byAbbrReq(key) {
    return (
      this.byAbbr(key) ||
      a.panic(Error(`unable to find ${this.type()} with abbr ${a.show(key)}`))
    )
  }

  bySlug(key) {return vacSome(key) && this.slugIndex[a.reqStr(key)]}

  bySlugReq(key) {
    return (
      this.bySlug(key) ||
      a.panic(Error(`unable to find ${this.type()} with slug ${a.show(key)}`))
    )
  }

  isValidValue(key) {return !!this.byValue(key)}
  isValidAbbr(key) {return !!this.byAbbr(key)}
  isValidSlug(key) {return !!this.bySlug(key)}
}

// TODO move to some more general utils.
function vacSome(val) {return a.vac(a.isSome(val))}

export class Month extends a.Struct {
  static spec = {
    value: a.reqNat,
    abbr: a.reqValidStr,
    slug: a.reqValidStr,
    title: a.reqValidStr,
  }

  pk() {return this.value}

  toString() {return a.title(this.title)}

  // Order and `.value` conform to JS `Date` API.
  static jan = new this({value: 0,  abbr: `jan`, slug: `january`,   title: `January`})
  static feb = new this({value: 1,  abbr: `feb`, slug: `february`,  title: `February`})
  static mar = new this({value: 2,  abbr: `mar`, slug: `march`,     title: `March`})
  static apr = new this({value: 3,  abbr: `apr`, slug: `april`,     title: `April`})
  static may = new this({value: 4,  abbr: `may`, slug: `may`,       title: `May`})
  static jun = new this({value: 5,  abbr: `jun`, slug: `june`,      title: `June`})
  static jul = new this({value: 6,  abbr: `jul`, slug: `july`,      title: `July`})
  static aug = new this({value: 7,  abbr: `aug`, slug: `august`,    title: `August`})
  static sep = new this({value: 8,  abbr: `sep`, slug: `september`, title: `September`})
  static oct = new this({value: 9,  abbr: `oct`, slug: `october`,   title: `October`})
  static nov = new this({value: 10, abbr: `nov`, slug: `november`,  title: `November`})
  static dec = new this({value: 11, abbr: `dec`, slug: `december`,  title: `December`})
}

export class Months extends CalEnum {
  get cls() {return Month}
  type() {return `month`}

  // Order and `.value` conform to JS `Date` API.
  static all = this.of(
    Month.jan,
    Month.feb,
    Month.mar,
    Month.apr,
    Month.may,
    Month.jun,
    Month.jul,
    Month.aug,
    Month.sep,
    Month.oct,
    Month.nov,
    Month.dec,
  )
}

export class Weekday extends a.Struct {
  static spec = {
    value: a.reqNat,
    abbr: a.reqValidStr,
    slug: a.reqValidStr,
    title: a.reqValidStr,
  }

  pk() {return this.value}
  char() {return a.reqStr(this.abbr[0])}

  toString() {return a.title(this.title)}

  // Order and `.value` conform to JS `Date` API.
  // Reference: https://tc39.es/ecma262/#sec-week-day.
  static sun = new this({value: 0, abbr: `sun`, slug: `sunday`,    title: `Sunday`})
  static mon = new this({value: 1, abbr: `mon`, slug: `monday`,    title: `Monday`})
  static tue = new this({value: 2, abbr: `tue`, slug: `tuesday`,   title: `Tuesday`})
  static wed = new this({value: 3, abbr: `wed`, slug: `wednesday`, title: `Wednesday`})
  static thu = new this({value: 4, abbr: `thu`, slug: `thursday`,  title: `Thursday`})
  static fri = new this({value: 5, abbr: `fri`, slug: `friday`,    title: `Friday`})
  static sat = new this({value: 6, abbr: `sat`, slug: `saturday`,  title: `Saturday`})
}

export class Weekdays extends CalEnum {
  get cls() {return Weekday}
  type() {return `weekday`}

  // Order and `.value` conform to JS `Date` API.
  static all = this.of(
    Weekday.sun,
    Weekday.mon,
    Weekday.tue,
    Weekday.wed,
    Weekday.thu,
    Weekday.fri,
    Weekday.sat,
  )

  // TODO: might want to support configurable first-day-of-week for muricans.
  firstValue() {return Weekday.mon.value}
  lastValue() {return this.cycleValue(this.firstValue(), -1)}
  first() {return this.byValueReq(this.firstValue())}
  last() {return this.byValueReq(this.lastValue())}

  // Short for "normalize".
  // Always returns a valid weekday value.
  // TODO simplify.
  norm(val) {return ((a.reqInt(val) % this.size) + this.size) % this.size}

  // Inverse of `.valueDelta`.
  // Always returns a valid weekday value.
  cycleValue(src, mod) {return this.norm(a.reqInt(src) + a.reqInt(mod))}

  // Inverse of `.cycleValue`.
  // Always returns a valid weekday value.
  valueDelta(src, tar) {return this.norm(a.reqInt(tar) - a.reqInt(src))}

  fromFirst() {
    const first = this.firstValue()
    const src = this.$
    const ind = src.findIndex(function test(val) {return val.value === first})
    return ind >= 0 ? src.slice(ind).concat(src.slice(0, ind)) : src
  }
}

/*
Used by `YearMonth` and `YearMonthDay`.

Value of `.month` should normally be within the range [0,11] (NOT [1,12]), but
other integers are also allowed. When instantiating a date-time, out-of-range
values are normalized, adjusting year and month as needed.
*/
class YearMonthBase extends a.Emp {
  constructor(year, month) {
    super()
    this.year = a.reqInt(year)
    this.month = a.reqInt(month)
  }

  get DateTime() {return DateTime}

  static now() {return this.fromDateTime(Date.now())}

  static fromDateTime(src) {
    src = a.toInst(src, this.prototype.DateTime)
    return new this(src.getFullYear(), src.getMonth())
  }
}

/*
Represents yyyy-MM without time or timezone information. Useful for calendar
operations.
*/
export class YearMonth extends YearMonthBase {
  get YearMonthDay() {return YearMonthDay}

  toString() {return a.spaced(this.year, this.enumMonth())}
  firstDateTime() {return new this.DateTime(this.year, this.month, 1)}
  lastDateTime() {return this.firstDateTime().addMonth(1).addDate(-1)}
  lastDate() {return this.lastDateTime().getDate()}
  dayRange() {return a.range(1, this.lastDate() + 1)}
  enumMonth() {return Months.all.byValueReq(this.month)}
  prev() {return this.firstDateTime().addMonth(-1).yearMonth()}
  next() {return this.firstDateTime().addMonth(1).yearMonth()}

  eq(val) {
    return true
      && a.isInst(val, YearMonth)
      && this.year === val.year
      && this.month === val.month
  }

  dayRangeToYearMonthDays(src) {
    return a.arr(src).map(this.dayOfMonthToYearMonthDay, this)
  }

  dayOfMonthToYearMonthDay(src) {
    return new this.YearMonthDay(this.year, this.month, src)
  }

  firstIncompleteWeekDayRange() {
    const weekdays = Weekdays.all
    const first = this.firstDateTime()
    const day = first.getDate()

    return a.range(
      day,
      day + weekdays.valueDelta(first.getDay(), weekdays.firstValue()),
    )
  }

  lastIncompleteWeekDayRange() {
    const weekdays = Weekdays.all
    const last = this.lastDateTime()
    const day = last.getDate()

    return a.range(
      day + 1 - weekdays.valueDelta(weekdays.lastValue(), last.getDay()),
      day + 1,
    )
  }

  /*
  Example output for September, padded with August at the start and October
  at the end. Each scalar is `YearMonthDay`.

    [
      A_28, A_29, A_30, A_31, S_01, S_02, S_03,
      S_04, S_05, S_06, S_07, S_08, S_09, S_10,
      S_11, S_12, S_13, S_14, S_15, S_16, S_17,
      S_18, S_19, S_20, S_21, S_22, S_23, S_24,
      S_25, S_26, S_27, S_28, S_29, S_30, O_01,
    ]

  Partially redundant with `weeklyDateRangeEnclosingMonth`. TODO dedup.
  */
  paddedDayRange() {
    const prev = this.prev()
    const next = this.next()

    return a.concat(
      prev.dayRangeToYearMonthDays(prev.lastIncompleteWeekDayRange()),
      this.dayRangeToYearMonthDays(this.dayRange()),
      next.dayRangeToYearMonthDays(next.firstIncompleteWeekDayRange()),
    )
  }

  /*
  Example output for September, padded with August at the start and October
  at the end. Each row is one week. Each scalar is `YearMonthDay`.

    [
      [A_28, A_29, A_30, A_31, S_01, S_02, S_03],
      [S_04, S_05, S_06, S_07, S_08, S_09, S_10],
      [S_11, S_12, S_13, S_14, S_15, S_16, S_17],
      [S_18, S_19, S_20, S_21, S_22, S_23, S_24],
      [S_25, S_26, S_27, S_28, S_29, S_30, O_01],
    ]

  Partially redundant with `weeklyDateRangeEnclosingMonth` and
  `weeklyDateRange`. TODO dedup.
  */
  paddedWeeks() {
    return uc.chunks(this.paddedDayRange(), Weekdays.all.size)
  }
}

/*
Represents yyyy-MM-dd without time or timezone information. Useful for calendar
operations.

Value of `.day` should normally be within the range [1,max] where max is within
the range [28,31] depending on the month, but other integers are also allowed.
When instantiating a date-time, out-of-range values are normalized, adjusting
year / month / day as needed. To produce `YearMonthDay` with normalized values,
call `.norm()` after constructing the instance.

TODO consider if this should be `YearMonthDate` rather than `YearMonthDay`.
The JS `Date` API uses the word "date" to mean "day of month", and uses the
word "day" to mean "day of week". But the naming they chose is insane and
error-prone to use.
*/
export class YearMonthDay extends YearMonthBase {
  constructor(year, month, day) {
    super(year, month)
    this.day = a.reqInt(day)
  }

  get YearMonth() {return YearMonth}

  static fromDateTime(src) {
    src = a.toInst(src, this.prototype.DateTime)
    return new this(src.getFullYear(), src.getMonth(), src.getDate())
  }

  static fuzzy(year, month, day) {
    if (a.isSome(year) && a.isSome(month) && a.isSome(day)) {
      return new this(year, month, day)
    }
    if (a.isSome(year) && a.isSome(month)) {
      return new this(year, month, 1)
    }
    if (a.isSome(year)) {
      return new this(year, 0, 1)
    }
    return undefined
  }

  toISOString() {return this.dateTime().dateStr()}
  toString() {return this.toISOString()}
  toJSON() {return this.toISOString()}
  clone() {return new this.constructor(this.year, this.month, this.day)}
  dateTime() {return new this.DateTime(this.year, this.month, this.day)}
  yearValid() {return this.dateTime().getFullYear()}
  monthValid() {return this.dateTime().getMonth()}
  dayValid() {return this.dateTime().getDate()}
  enumMonth() {return Months.all.byValueReq(this.monthValid())}
  enumWeekday() {return this.dateTime().enumWeekday()}
  yearMonth() {return new this.YearMonth(this.year, this.month)}
  prev() {return this.dateTime().addDate(-1).yearMonthDay()}
  next() {return this.dateTime().addDate(1).yearMonthDay()}
  prevMonth() {return this.dateTime().addMonth(-1).yearMonthDay()}
  nextMonth() {return this.dateTime().addMonth(1).yearMonthDay()}
  prevYear() {return this.dateTime().addYear(-1).yearMonthDay()}
  nextYear() {return this.dateTime().addYear(1).yearMonthDay()}

  eq(val) {
    return true
      && a.isInst(val, YearMonthDay)
      && this.year === val.year
      && this.month === val.month
      && this.day === val.day
  }

  // Short for "normalize".
  norm() {
    const src = this.dateTime()
    this.year = src.getFullYear()
    this.month = src.getMonth()
    this.day = src.getDate()
    return this
  }
}

export function relTime(now, date, opt) {
  const delta = now - date
  const isLong = opt === `long`

  if (a.isNaN(delta)) return undefined

  if (Math.abs(delta) < a.MS_IN_MIN) {
    return a.str(uma.round(delta / a.MS_IN_SEC), (isLong ? ` seconds` : `s`))
    // return 'now'
  }

  if (Math.abs(delta) < a.MS_IN_HOUR) {
    return a.str(uma.round(delta / a.MS_IN_MIN), (isLong ? ` minutes` : `m`))
  }

  if (Math.abs(delta) < a.MS_IN_DAY) {
    return a.str(uma.round(delta / a.MS_IN_HOUR), (isLong ? ` hours` : `h`))
  }

  if (Math.abs(delta) < a.MS_IN_WEEK) {
    return a.str(uma.round(delta / a.MS_IN_DAY), (isLong ? ` days` : `d`))
  }

  if (Math.abs(delta) < a.MS_IN_MON) {
    return a.str(uma.round(delta / a.MS_IN_WEEK), (isLong ? ` weeks` : `w`))
  }

  if (Math.abs(delta) < a.MS_IN_YEAR) {
    return a.str(uma.round(delta / a.MS_IN_MON), (isLong ? ` months` : `mo`))
  }

  return a.str(uma.round(delta / a.MS_IN_YEAR), (isLong ? ` years` : `y`))
}

/*
TODO: if we ever use this function, calculate the difference from methods such
as `.getUTCFullYear()`, `.getUTCMonth()` and so on, instead of multiplying
milliseconds.

TODO: also calculate months and return output as `Dur`.
*/
export function dateDiff(now, date) {
  a.reqDate(now)
  a.reqDate(date)

  const diff = date - now

  const seconds = Math.trunc(((((diff % a.MS_IN_YEAR) % a.MS_IN_DAY) % a.MS_IN_HOUR) % a.MS_IN_MIN) / a.MS_IN_SEC)
  const minutes = Math.trunc((((diff % a.MS_IN_YEAR) % a.MS_IN_DAY) % a.MS_IN_HOUR) / a.MS_IN_MIN)
  const hours = Math.trunc(((diff % a.MS_IN_YEAR) % a.MS_IN_DAY) / a.MS_IN_HOUR)
  const days = Math.trunc((diff % a.MS_IN_YEAR) / a.MS_IN_DAY)
  const years = Math.trunc(diff / a.MS_IN_YEAR)

  return {seconds, minutes, hours, days, years}
}

export function toDate(val) {return a.toInst(val, Date)}
export function toValidDate(val) {return a.toInst(val, DateTime)}

/*
The implementation is inconsistent with other "opt" functions in order to
support both primitive and non-primitive inputs.
*/
export function toValidDateOpt(val) {
  if (a.isNil(val)) return undefined
  if (a.isNum(val)) return a.isFin(val) ? new DateTime(val) : undefined
  if (a.isStr(val)) return toValidDateOpt(Date.parse(val))
  if (a.isDate(val)) return a.isValidDate(val) ? a.toInstOpt(val, DateTime) : undefined
  throw a.errConv(val, `valid date`)
}

// TODO remove.
export function toTimestamp(val) {return a.tsOpt(val)}

// TODO remove.
export function toValidTimestamp(val) {return a.ts(val)}

// Inefficient, TODO remove.
export function isConvertibleToValidTime(val) {return a.isFin(a.tsOpt(val))}

/*
Values outside this range may be misinterpreted by JS engines when converting a
timestamp into a date, for example via `new Date(val)`. TODO: check if the
language spec says anything about this.
*/
export function isValidMilliTimestamp(val) {
  return a.isFin(val) && val >= -1e+12 && val <= 1e+12
}

export function millisToSecs(val) {return a.optNum(val) && a.msToSec(val)}
export function secsToMillis(val) {return a.optNum(val) && a.secToMs(val)}
export function secsToMins(val) {return a.optNum(val) && a.secToMin(val)}
export function secsToHours(val) {return a.optNum(val) && a.secToHour(val)}
export function minsToSecs(val) {return a.optNum(val) && a.minToSec(val)}
export function minsToMillis(val) {return a.optNum(val) && a.minToMs(val)}
export function hoursToSecs(val) {return a.optNum(val) && a.hourToSec(val)}
export function secsToWholeHours(val) {return a.optNum(val) && Math.trunc(a.secToHour(val))}

export function secsToWholeRemMins(val) {
  return a.optNum(val) && uma.round((val % a.SEC_IN_HOUR) / a.SEC_IN_MIN)
}

// Should be supported in all of our target browsers.
export const systemTimezoneName = globalThis.Intl.DateTimeFormat().resolvedOptions().timeZone

/*
Timezone offset of system clock, in minutes/

Note: in some JS environments, such as Chrome, the timezone offset may update on
the fly even for existing date objects. In some others, such as Node(checked in
v13.2.0), the timezone offset may be fixed on process startup. We snapshot this
at the start for consistency with `systemTimezoneName`.
*/
export const systemTimezoneOffset = new Date().getTimezoneOffset()

/*
Used in various datetime functions to determine whether to run calculations in
UTC time or local time. By convention, `tzm` should be passed as the last
argument to a function:

  dateSomething(val, arg, c.TZMODE_UTC)
  dateSomething(val, arg, c.TZMODE_LOCAL)

Note that by another convention, with the exception of view components, this
codebase tends to avoid optional or default parameters. All parameters and
arguments must be explicit, with no hidden defaults.

Note on naming: this is called `tzmodeIsUtc` rather than `isTzmodeUtc`, because
following the naming conventions in `@mitranim/js`, `isTzmodeUtc` would return
false for anything other than UTC timezone mode. This has different behavior
(much stricter) and requires a different naming convention.
*/
export function tzmodeIsUtc(tzm) {
  if (tzm === c.TZMODE_UTC) return true
  if (tzm === c.TZMODE_LOCAL) return false
  throw Error(`unrecognized timezone mode ${a.show(tzm)}, expected ${a.show(c.TZMODE_UTC)} or ${a.show(c.TZMODE_LOCAL)}`)
}

/*
Extremely naive implementation that hardcodes Saturday and Sunday as weekend
days, which is true in most of the world. Note that in the US, the week "begins"
on Sunday, but the _weekend_ is still Saturday and Sunday. This should be good
enough for most of our users.

In a more general case, this should be based on the geographic location. See
https://en.wikipedia.org/wiki/Weekend for the various exceptions.
*/
export function isWeekendWeekday(val) {
  return val === Weekday.sun.value || Weekday.sat.value
}

/*
Reference: https://tc39.es/ecma262/#sec-week-day.

Note: `a.isNat` allows 0.
*/
export function isJsWeekday(val) {return a.isNat(val) && val <= 6}

export function dateToWeekdaySlug(val, tzm) {
  return dateToWeekday(val, tzm).slug
}

export function dateToWeekday(val, tzm) {
  const date = toValidDate(val)

  return Weekdays.all.byValueReq(
    tzmodeIsUtc(tzm) ? date.getUTCDay() : date.getDay()
  )
}

export function monthTitle(val) {return Months.all.byValue(val)?.title}

export function dateDayStart(val, tzm) {
  return dateDayStartWithDelta(val, 0, tzm)
}

export function dateDayStartWithDelta(val, delta, tzm) {
  const date = toValidDate(val)
  a.reqInt(delta)
  if (tzmodeIsUtc(tzm)) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + delta))
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + delta)
}

export function datePrevDayStart(val, tzm) {
  return dateDayStartWithDelta(val, -1, tzm)
}

export function dateNextDayStart(val, tzm) {
  return dateDayStartWithDelta(val, 1, tzm)
}

export function dateMonthStart(val, tzm) {
  return dateMonthStartWithDelta(val, 0, tzm)
}

export function dateMonthStartWithDelta(val, delta, tzm) {
  const date = toValidDate(val)
  a.reqInt(delta)
  if (tzmodeIsUtc(tzm)) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + delta, 1))
  }
  return new Date(date.getFullYear(), date.getMonth() + delta, 1)
}

export function datePrevMonthStart(val, tzm) {
  return dateMonthStartWithDelta(val, -1, tzm)
}

export function dateNextMonthStart(val, tzm) {
  return dateMonthStartWithDelta(val, 1, tzm)
}

export function dateAdjustToWeekdayUp(val, weekday, tzm) {
  return dateAdjustToWeekday(val, weekday, a.inc, tzm)
}

export function dateAdjustToWeekdayDown(val, weekday, tzm) {
  return dateAdjustToWeekday(val, weekday, a.dec, tzm)
}

function dateAdjustToWeekday(val, weekday, modDay, tzm) {
  // Make a copy because we mutate it below.
  const date = new DateTime(val)

  // const jsWeekday = weekdayToMeta(weekday).jsValue
  const jsWeekday = Weekdays.all.bySlugReq(weekday).value
  a.req(jsWeekday, isJsWeekday)

  if (tzmodeIsUtc(tzm)) {
    while (date.getUTCDay() !== jsWeekday) {
      date.setUTCDate(modDay(date.getUTCDate()))
    }
  }
  else {
    while (date.getDay() !== jsWeekday) {
      date.setDate(modDay(date.getDate()))
    }
  }

  return date
}

/*
Returns true if both dates have the same internal val or if both are invalid.
Doesn't care about the timezone.
*/
export function dateEqual(left, right) {
  return a.is(a.tsOpt(left), a.tsOpt(right))
}

export function dateEqualYear(left, right, tzm) {
  return (
    a.isDate(left) &&
    a.isDate(right) &&
    dateGetYear(left, tzm) === dateGetYear(right, tzm)
  )
}

export function dateEqualMonth(left, right, tzm) {
  return (
    dateEqualYear(left, right, tzm) &&
    dateGetMonth(left, tzm) === dateGetMonth(right, tzm)
  )
}

export function dateEqualDay(left, right, tzm) {
  return (
    dateEqualMonth(left, right, tzm) &&
    dateGetDay(left, tzm) === dateGetDay(right, tzm)
  )
}

export function isToday(val, tzm) {
  return isSameDay(val, Date.now(), tzm)
}

/*
Note: a more efficient implementation would avoid allocating intermediary date
objects, but this requires too much extra code. This could be both short and
efficient if the rest of our date functions operated on timestamps rather than
objects.
*/
export function isSameDay(one, two, tzm) {
  return dateDayStart(one, tzm).valueOf() === dateDayStart(two, tzm).valueOf()
}

export function isPastDay(val, tzm) {
  return isDayBefore(val, Date.now(), tzm)
}

export function isDayBefore(next, prev, tzm) {
  return dateDayStart(next, tzm).valueOf() < dateDayStart(prev, tzm).valueOf()
}

/*
Creates a sequence of dates for a typical calendar monthly view, where A, B, C
are different months:

  A29  A30  A31  B01  B02  B03  B04

  B05  B06  B07  B08  B09  B10  B11

  B12  B13  B14  B15  B16  B17  B18

  B19  B20  B21  B22  B23  B24  B25

  B26  B27  B28  B29  B30  C01  C02

The provided date determines the main year and month; its day and time is
irrelevant. The provided weekday determines the start of the week, like Monday
or Sunday.

Each returned date is the beginning of a day, with time fields set to zero
either in UTC or local time, depending on `tzm`.

Partially redundant with `YearMonth..paddedDayRange`. TODO dedup.

Also compare `weeklyDateRange`.
*/
export function weeklyDateRangeEnclosingMonth(date, firstWeekday, tzm) {
  const start = dateToMinMonthStartWeekday(date, firstWeekday, tzm)
  const nextStart = dateToMaxNextMonthStartWeekday(date, firstWeekday, tzm)
  return dateRange(start, nextStart, tzm)
}

// WTB better name.
function dateToMinMonthStartWeekday(date, firstWeekday, tzm) {
  const monthStart = dateMonthStart(date, tzm)
  return dateAdjustToWeekdayDown(monthStart, firstWeekday, tzm)
}

// WTB better name.
function dateToMaxNextMonthStartWeekday(date, firstWeekday, tzm) {
  const nextStart = dateNextMonthStart(date, tzm)
  return dateAdjustToWeekdayUp(nextStart, firstWeekday, tzm)
}

/*
Similar to `weeklyDateRangeEnclosingMonth`, but week-oriented rather than
month-oriented. Creates a sequence of days divisible by the weekday count (7):

  A29  A30  A31  B01  B02  B03  B04

  B05  B06  B07  B08  B09  B10  B11

  ... more

The difference from `weeklyDateRangeEnclosingMonth` is that the provided date is
always in the "first row" (first `Weekdays.all.size` elements), and the number
of "rows" is equal to the provided `weekCount`; in other words, the list's
length equals `weekCount * Weekdays.all.size`.

Partially redundant with `YearMonth..paddedDayRange`. TODO dedup.
*/
export function weeklyDateRange(date, firstWeekday, weekCount, tzm) {
  a.reqNat(weekCount)
  const start = dateAdjustToWeekdayDown(date, firstWeekday, tzm)
  const nextStart = dateDayStartWithDelta(start, (weekCount * Weekdays.all.size), tzm)
  return dateRange(start, nextStart, tzm)
}

/*
Creates a sequence of dates between `start` and `nextStart`, inclusive at the
start and exclusive at the nextStart: [start,nextStart). Guarantees the
following:

  * Each date is zero-timed in accordance with `tzm`: 00:00:00 either in UTC
    time or in local time.
  * Days follow each other.

Both `start` and `nextStart` are also round down in accordance with `tzm`.

Beware: in case of DST, the length of some days may not equal 24 hours!

TODO: consider making a version of this function that generates timestamps
instead of date objects, avoiding date allocations when timestamps are
sufficient or preferable (for example, in view component props). This would
require duplicating a few other utilities into timestamp-oriented versions.
Utilities such as `dateDayStartWithDelta` would need additional
input-date-mutating versions since they can't operate purely on timestamps.
*/
function dateRange(start, nextStart, tzm) {
  a.reqValidDate(start)
  a.reqValidDate(nextStart)

  start = dateDayStart(start, tzm)
  nextStart = dateDayStart(nextStart, tzm)

  const dates = []

  let cursor = start
  while (cursor.valueOf() < nextStart.valueOf()) {
    dates.push(cursor)
    cursor = dateDayStartWithDelta(cursor, 1, tzm)
  }

  return dates
}

export function dateGetYear(val, tzm) {
  const date = toValidDate(val)
  return tzmodeIsUtc(tzm) ? date.getUTCFullYear() : date.getFullYear()
}

export function dateGetMonth(val, tzm) {
  const date = toValidDate(val)
  return tzmodeIsUtc(tzm) ? date.getUTCMonth() : date.getMonth()
}

/*
`Day` in this name is inconsistent with JS method names, but consistent with our
own functions; we generally use the 'Year-Month-Day & Weekday' nomenclature
rather than the idiotic 'FullYear-Month-Date & Day'.
*/
export function dateGetDay(val, tzm) {
  const date = toValidDate(val)
  return tzmodeIsUtc(tzm) ? date.getUTCDate() : date.getDate()
}

export function dateGetJsWeekday(val, tzm) {
  const date = toValidDate(val)
  return tzmodeIsUtc(tzm) ? date.getUTCDay() : date.getDay()
}

export function isDateBetween(val, start, end) {
  if (a.isNil(val) || a.isNil(start)) return false
  if (a.isNil(end)) return true
  val = toValidTimestamp(val)
  return val >= toValidTimestamp(start) && val <= toValidTimestamp(end)
}

export function hoursAgo(val) {
  const timestamp = toTimestamp(val)
  if (!a.isFin(timestamp)) return 0
  return uma.round((Date.now() - timestamp) / a.MS_IN_HOUR) || 0
}

export function dateFromYear(year) {
  return new Date(Date.UTC(a.laxInt(year)))
}

export class Timeout extends a.Emp {
  constructor(time, fun, ...args) {super().id = setTimeout(fun, time, ...args)}
  deinit() {clearTimeout(this.id)}
}

export class Interval extends a.Emp {
  constructor(time, fun, ...args) {super().id = setInterval(fun, time, ...args)}
  deinit() {clearInterval(this.id)}
}

// TODO deduplicate with `Debounce` (choose one, remove other).
export function debounce(time, fun) {
  a.reqNat(time)
  a.reqFun(fun)

  let timerId
  function deinit() {clearTimeout(timerId)}

  function debounced(...args) {
    clearTimeout(timerId)
    timerId = setTimeout(fun, time, ...args)
    return deinit
  }

  debounced.deinit = deinit
  return debounced
}

// Tentative. Too much code.
// TODO deduplicate with `debounce` (choose one, remove other).
export class Debounce extends a.Emp {
  constructor(ref) {
    a.opt(ref, isDebounceable)
    super()
    this.ref = ref
    this.timer = undefined
  }

  isPending() {return !!this.timer}

  delay(delay) {
    a.reqNat(delay)
    this.clear()
    this.timer = setTimeout(debounced, delay, this)
  }

  debounced() {
    this.clear()
    this.ref?.debounced()
  }

  clear() {
    if (!this.timer) return
    clearTimeout(this.timer)
    this.timer = undefined
  }

  deinit() {this.clear()}
}

function isDebounceable(val) {return a.isComp(val) && a.isFun(val.debounced)}
function debounced(val) {val.debounced()}

export function since(time) {return Date.now() - a.laxFin(a.tsOpt(time))}

export function dur(val) {return a.toInst(val, Dur)}
export function durOpt(val) {return a.toInstOpt(val, Dur)}

// Very approximate.
export function msToWeeks(val) {return Math.trunc(a.laxFin(val) / a.MS_IN_WEEK)}

/*
Short for "duration". Can be constructed from a standard ISO string
(see reference) or from a struct.

Reference: https://en.wikipedia.org/wiki/ISO_8601#Durations
*/
export class Dur extends a.Dur {
  toString(opt) {return a.spaced(this.dateString(opt), this.timeString(opt))}

  dateStringAdjective() {return this.dateString({isAdj: true})}

  dateString(opt) {
    const {years, months, days} = this

    if (opt?.isLong) {
      const force = this.shouldShowNumber()
      return a.spaced(
        ut.plural(years,  `year`,  force, opt?.isAdj),
        ut.plural(months, `month`, force, opt?.isAdj),
        ut.plural(days,   `day`,   force, opt?.isAdj),
      )
    }

    return a.spaced(
      a.vac(years) && a.str(years, `y`),
      a.vac(months) && a.str(months, `mo`),
      a.vac(days) && a.str(days, `d`),
    )
  }

  timeString(opt) {
    const {hours, minutes, seconds} = this
    if (!hours && !minutes && !seconds) return ``

    if (opt?.isClock) return ut.formatTime(hours, minutes, seconds)

    if (opt?.isLong) {
      const force = this.shouldShowNumber()
      return a.spaced(
        ut.plural(hours,   `hour`,   force, opt?.isAdj),
        ut.plural(minutes, `minute`, force, opt?.isAdj),
        ut.plural(seconds, `second`, force, opt?.isAdj),
      )
    }

    return a.spaced(
      a.vac(hours) && a.str(hours, `h`),
      a.vac(minutes) && a.str(minutes, `m`),
      a.vac(seconds) && a.str(seconds, `s`),
    )
  }

  shouldShowNumber() {return a.count(this, a.id) > 1}

  wholeMonths() {
    return (
      0
      + (this.years   * a.MON_IN_YEAR)
      + (this.months  * 1)
      + (this.days    / a.DAY_IN_MON)
      + (this.hours   / c.HOUR_IN_MON)
      + (this.minutes / c.MIN_IN_MON)
      + (this.seconds / c.SEC_IN_MON)
    )
  }

  wholeDays() {
    return (
      0
      + (this.years   * a.DAY_IN_YEAR)
      + (this.months  * a.DAY_IN_MON)
      + (this.days    / a.DAY_IN_MON)
      + (this.hours   / c.HOUR_IN_MON)
      + (this.minutes / c.MIN_IN_MON)
      + (this.seconds / c.SEC_IN_MON)
    )
  }

  isYearly() {return this.toISOString() === `P1Y`}
  isMonthly() {return this.toISOString() === `P1M`}
  isDaily() {return this.toISOString() === `P1D`}

  // TODO consider moving to `a.Dur`.
  resetFromRange(one, two) {return this.resetFromMilli(a.ts(two) - a.ts(one))}
}

export class DateTimeHuman extends DateTime {
  toString() {return this.constructor.getFmt().format(this)}

  static getFmt() {return a.getOwn(this, `fmt`) || (this.fmt = this.makeFmt())}

  // By luck, the Swedish locale mostly adheres to ISO 8601.
  static makeFmt() {
    return new ut.DateTimeFormat(`sv-SE`, {
      hour12: false,
      timeZone: `UTC`,
      timeZoneName: `short`,
      year: `numeric`,
      month: `2-digit`,
      day: `2-digit`,
      hour: `2-digit`,
      minute: `2-digit`,
    })
  }
}

export const dayHourOffsets = a.range(0, 24)

export function formatHour(val) {
  return a.reqNat(val).toString().padStart(2, `0`) + `:00`
}

/*
The function is permissive and allows `h`, `hh`, `hh:` because it's intended for
parsing incomplete user input.
*/
export function parseHoursMinutes(src) {
  const mat = ut.strTrim(src).match(RE_HOURS_MINUTES_LAX)
  if (!mat) return undefined

  return [
    ut.parseNum(mat?.[1]), // Never nil.
    ut.parseNum(mat?.[2]), // Sometimes nil.
  ]
}

/*
The regex is permissive and allows `h`, `hh`, `hh:` because it's intended for
parsing incomplete user input.
*/
export const RE_HOURS_MINUTES_LAX = /^(\d+)(?::(\d*))?$/

export function parseHoursMinutesAsSeconds(src) {
  const tar = parseHoursMinutes(src)
  return tar && (
    0
    + a.laxNat(hoursToSecs(tar[0]))
    + a.laxNat(minsToSecs(tar[1]))
  )
}

/*
Formats seconds as `hh:mm`. Note that if the input exceeds 24 hours, so does the
output.
*/
// TODO move to `util-time.mjs`.
export function formatSecondsAsHoursMinutes(src) {
  if (a.isNil(src)) return ``
  a.reqFin(src)
  return formatHoursMinutes(secsToWholeHours(src), secsToWholeRemMins(src))
}

export function formatHoursMinutes(hours, minutes) {
  return ut.padZero2(hours) + `:` + ut.padZero2(minutes)
}
